import { DetailsTileTitle } from "@/components/license-details/tile-info";
import { HistoryActorRow } from "@/components/shared/history-actor-row";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { LicenseResponseSchema } from "@/hera-api/client/types.gen";
import {
  type LicenseStatusChange,
  getLicenseHistory,
  licenseStatusChangesFromEvents,
} from "@/hera-api/license-history-api";
import { getLicenseStatusLabel, isLicenseStatus } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

function statusLabel(rawStatus: string | null): string {
  if (!rawStatus) return "Unknown";
  return isLicenseStatus(rawStatus)
    ? getLicenseStatusLabel(rawStatus)
    : rawStatus.replaceAll("_", " ");
}

function statusChangeLabel(change: LicenseStatusChange): string {
  const to = statusLabel(change.newStatus);
  if (change.isInitial) return `Created as ${to}`;
  return `${statusLabel(change.oldStatus)} → ${to}`;
}

/** Lifecycle actors for operator license details. */
export function LicenseRecordHistoryCard({
  data,
}: {
  data: Pick<
    LicenseResponseSchema,
    | "id"
    | "created_at"
    | "created_by_display"
    | "last_updated_at"
    | "last_updated_by_display"
    | "approved_at"
    | "approved_by_display"
    | "cancellation_date"
    | "cancelled_by_display"
  >;
}) {
  const fallbackActor = data.last_updated_by_display || data.created_by_display;
  const historyQuery = useQuery({
    queryKey: ["getLicenseHistory", data.id],
    queryFn: () => getLicenseHistory(data.id),
    enabled: Boolean(data.id),
  });
  const statusChanges = useMemo(
    () => licenseStatusChangesFromEvents(historyQuery.data?.events ?? []),
    [historyQuery.data],
  );

  return (
    <Card className="gap-4">
      <CardHeader>
        <DetailsTileTitle name="History" />
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <HistoryActorRow
          label="Created"
          when={data.created_at}
          actor={data.created_by_display}
          fallbackActor={fallbackActor}
        />
        <HistoryActorRow
          label="Last modified"
          when={data.last_updated_at}
          actor={data.last_updated_by_display}
          fallbackActor={data.created_by_display}
        />
        {data.approved_at ? (
          <HistoryActorRow
            label="Approved"
            when={data.approved_at}
            actor={data.approved_by_display}
            fallbackActor={fallbackActor}
          />
        ) : null}
        {data.cancellation_date ? (
          <HistoryActorRow
            label="Cancelled"
            when={data.cancellation_date}
            actor={data.cancelled_by_display}
            fallbackActor={fallbackActor}
            dateOnly
          />
        ) : null}
        <div className="space-y-3 border-t pt-3">
          <p className="font-medium">Status changes</p>
          {historyQuery.isLoading ? (
            <p className="text-muted-foreground">Loading status changes…</p>
          ) : historyQuery.isError ? (
            <p className="text-muted-foreground">Status changes unavailable.</p>
          ) : statusChanges.length === 0 ? (
            <p className="text-muted-foreground">
              No audited status changes found.
            </p>
          ) : (
            statusChanges.map((change) => (
              <HistoryActorRow
                key={change.eventId}
                label={statusChangeLabel(change)}
                when={change.changedAt}
                actor={change.actor}
                extra={change.actorUserType?.toLowerCase() ?? null}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
